import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { CheckCircle } from '@phosphor-icons/react';
import { Trash } from '@phosphor-icons/react/dist/ssr';
import { useEffect, useState } from 'react';
import { UploadService } from '../../../service/UploadService';
import { useAuthStore } from '../../../store/storeAuth';
import { useNotificationStore } from '../../../store/storeNotifications';
import { useSocketStore } from '../../../store/storeSocket';
import KitModal from '../../UI/KitModal';

const VectorMemoryUploadModal = ({
  openVectorUploadModal,
  setOpenVectorUploadModal,
  onUploadDocument,
  selectVector,
  onDeleteUploadedDocument,
  onDeleteNameSpace,
  flowId,
  nodeId,
  setNodeLoading,
}: any) => {
  const [tabs, setTabs] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    selectedDocument: '',
    selectedFluxPromptObject: '',
    fluxPromptObjectText: '',
  });
  const [finish, setFinish] = useState(false);
  const isEditing = openVectorUploadModal.type === 'edit';

  const setNotification = useNotificationStore(state => state.setNotification);
  const { user: authUser, documents } = useAuthStore(state => state);
  const [notifyModal, setNotifyModal] = useState(false);

  const [uploadedFiles, setUploadedFiles] = useState<File[] | null>([]);
  const [isLoading, setIsLoading] = useState<Boolean>(false);

  const [totalFiles, setTotalFiles] = useState<number>(0);
  const [messageWait, setMessageWait] = useState<string>('Uploading files...');
  const [cantCreateMemoryPod, setCantCreateMemoryPod] =
    useState<boolean>(false);

  const onChange = (e: any) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  const onHideModal = () => {
    setOpenVectorUploadModal({
      open: false,
      type: '',
    });
    setFormData({
      name: '',
      selectedDocument: '',
      selectedFluxPromptObject: '',
      fluxPromptObjectText: '',
    });

    setFinish(false);

    setTabs(0);
  };

  const handleUpload = () => {
    if (tabs === 0) {
      if (
        !uploadedFiles ||
        uploadedFiles.length === 0 ||
        (!formData.name && !isEditing)
      ) {
        alert(
          `Please select ${
            !uploadedFiles || uploadedFiles.length === 0 ? 'a file' : 'a name'
          } for your Memory Pod.`,
        );

        setFinish(false);
        setNotifyModal(false);
        return;
      }
      const files = uploadedFiles;

      onUploadDocument({
        vectorName: isEditing ? selectVector?.name : formData.name,
        files: files,
        type: 'upload-from-computer',
        formData: formData,
      });

      setUploadedFiles(null);
    }

    if (tabs === 1) {
      if (!formData.selectedDocument || (!formData.name && !isEditing)) {
        alert(
          `Please select a ${
            !formData.selectedDocument ? 'document' : 'name'
          } for your Memory Pod.`,
        );

        setFinish(false);
        setNotifyModal(false);
        return;
      }

      onUploadDocument({
        vectorName: isEditing ? selectVector?.name : formData.name,
        file: formData.selectedDocument,
        type: 'select-from-documents',
        formData: formData,
      });
    }

    if (tabs === 2) {
      if (
        !formData.selectedFluxPromptObject ||
        !formData.fluxPromptObjectText ||
        (!formData.name && !isEditing)
      ) {
        alert(
          `Please select a ${
            !formData.selectedFluxPromptObject ? 'Flux Prompt Object' : 'Object'
          } for your Memory Pod.`,
        );

        setFinish(false);
        setNotifyModal(false);
        return;
      }

      onUploadDocument({
        vectorName: isEditing ? selectVector?.name : formData.name,
        file: formData.fluxPromptObjectText, // Pass as an array to maintain consistency
        type: 'select-from-fluxprompt-object',
        formData: formData,
      });
    }

    setFinish(true);
  };

  const { socket } = useSocketStore(state => state);

  const onChangeUploadFiles = async (e: any) => {
    try {
      setNotification({
        type: 'info',
        message: 'Extracting text from the file',
      });

      setIsLoading(true);

      const files = Array.from(e?.target?.files) as File[];

      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });

      setNotification({
        type: 'info',
        message: 'Uploading files...',
      });

      const response = await UploadService.extractTextFromFiles(
        socket?.id!,
        nodeId,
        flowId,
        formData,
      );

      if (response?.status === 'success') {
        setNotification({
          message: response.data.message,
          type: 'success',
        });

        setCantCreateMemoryPod(true);

        setIsLoading(true);

        if (setNodeLoading) setNodeLoading(true);

        setNotification({
          type: 'info',
          message: 'Extracting text from files...',
          duration: 21600000,
        });

        setMessageWait('Extracting text from files...');

        if (response?.data?.totalFiles) {
          setTotalFiles(totalFiles + parseInt(response?.data?.totalFiles));
        }
      } else {
        setIsLoading(false);
        setNotification({
          type: 'error',
          message: 'Failed to extract text from files',
        });
      }
    } catch (error: any) {
      setIsLoading(false);
      setNotification({
        type: 'error',
        message: error.message || 'Failed to extract text from files',
        duration: 8000,
      });
    }
  };

  useEffect(() => {
    const textDecoder = new TextDecoder('utf-8');

    const handleExtractTextResponse = async (res: any) => {
      if (res.data) {
        const { file: rawFile, fileName } = res.data;

        const type = rawFile ? rawFile.split('.')[1] : 'pdf';

        const file =
          rawFile.buffer instanceof ArrayBuffer
            ? textDecoder.decode(rawFile.buffer)
            : rawFile;

        const originalFileName = fileName;
        const uniqueSuffix = Date.now();

        const blob = new Blob([file], { type: 'text/plain' });
        const txtFile = new File(
          [blob],
          `extracted-${type}-${originalFileName}-${uniqueSuffix}.txt`,
          { type: 'text/plain' },
        );

        setUploadedFiles(prevFiles =>
          prevFiles ? [...prevFiles, txtFile] : [txtFile],
        );
      }
    };

    if (socket?.id) {
      socket?.on(`extract-text-${nodeId}`, handleExtractTextResponse);
    }

    return () => {
      socket?.off(`extract-text-${nodeId}`, handleExtractTextResponse);
    };
  }, [socket?.id, formData, uploadedFiles]);

  useEffect(() => {
    if (uploadedFiles?.length === totalFiles && uploadedFiles?.length > 0) {
      setCantCreateMemoryPod(false);
      setIsLoading(false);
      setNotification({
        type: 'success',
        message: 'VectorMemory - Extracted texts from all files successfully!',
      });
      setMessageWait('Uploading files...');
      if (setNodeLoading) setNodeLoading(false);
    }
  }, [uploadedFiles, totalFiles]);

  return (
    <>
      <KitModal
        show={notifyModal}
        maxWidth={'md'}
        onHide={() => {
          setNotifyModal(false);
          setFinish(false);
        }}
        showCloseButton={false}
        sx={{
          '& .MuiPaper-root': {
            maxWidth: '668px',
            borderRadius: '16px',
          },
        }}
      >
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            {finish && <CheckCircle size={25} weight="fill" color="#4CAF50" />}
            <Typography fontSize={'20px'} variant="h6">
              {finish
                ? 'Documents are being uploaded!'
                : 'Proceed to add document'}
            </Typography>
          </Box>

          <Typography mt={'5px'} variant="body2">
            {finish
              ? 'Depending on size of documents, please allow up to an hour for documents to upload to the Large Memory Pod.'
              : ` Once document is uploaded to a Memory Pod, they cannot be edited nor
            removed. Would you like to proceed to add document?`}
          </Typography>

          <Box
            display={'flex'}
            alignItems={'center'}
            gap={'10px'}
            justifyContent={'end'}
            mt={'60px'}
          >
            {!finish && (
              <Button onClick={onHideModal} variant="outlined">
                Cancel
              </Button>
            )}
            <Button
              sx={{
                boxShadow: 'none',
              }}
              onClick={() => {
                if (!finish) {
                  handleUpload();
                } else {
                  setNotifyModal(false);
                  onHideModal();
                }
              }}
              variant="contained"
            >
              {finish ? 'Dismiss' : 'Add Doc'}
            </Button>
          </Box>
        </Box>
      </KitModal>

      <KitModal
        show={openVectorUploadModal.open}
        maxWidth={'md'}
        sx={{
          '& .MuiPaper-root': {
            maxWidth: '668px',
            borderRadius: '16px',
          },
        }}
        onHide={onHideModal}
      >
        <>
          <Typography
            fontSize={'20px'}
            variant="h6"
            color={authUser?.theme === 'dark' ? '#D0D5DD' : 'black'}
          >
            {isEditing
              ? `Edit ${selectVector?.name}`
              : 'Create a Large Memory Pod'}
          </Typography>

          <Typography
            mt={'5px'}
            variant="body2"
            color={authUser?.theme === 'dark' ? '#D0D5DD' : 'black'}
          >
            {isEditing
              ? 'Documents already uploaded.'
              : `Create a memory pod and upload documents into your pod. Then, use the
          Large Memory Node to easily query the documents and receive answers to
          your questions.`}
          </Typography>

          {isEditing && (
            <Box mt={'24px'}>
              {selectVector?.files?.map((file: any, i: any) => (
                <Chip
                  sx={{
                    borderRadius: '4px',
                    color: '#3f51b5',
                    borderColor: '#3f51b5',
                  }}
                  deleteIcon={
                    <Trash color="#d32f2f" weight="duotone" size={14} />
                  }
                  onDelete={() => onDeleteUploadedDocument(file)}
                  key={i}
                  label={file}
                  variant="outlined"
                />
              ))}
            </Box>
          )}

          {!isEditing && (
            <TextField
              label="Name your Memory Pod"
              fullWidth
              name="name"
              value={formData.name}
              onChange={onChange}
              sx={{
                mt: '24px',
                '& .MuiInputLabel-root': {
                  color: '#9e9e9e',
                },
              }}
              variant="outlined"
              InputProps={{
                sx: {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#9e9e9e',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#9e9e9e',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#9e9e9e',
                  },
                },
              }}
              InputLabelProps={{
                sx: {
                  color: '#9e9e9e',
                },
              }}
            />
          )}

          <Typography
            mt={'24px'}
            variant="h6"
            color={authUser?.theme === 'dark' ? '#D0D5DD' : 'black'}
          >
            {isEditing
              ? 'Add new documents into memory pod'
              : 'Upload documents into memory pod'}
          </Typography>

          <Typography
            mt={'5px'}
            variant="body2"
            color={authUser?.theme === 'dark' ? '#D0D5DD' : 'black'}
          >
            Choose the documents you want to put into your memory pod. You can
            select FluxPrompt objects, documents uploaded to FluxPrompt, or
            documents from your computer.
          </Typography>

          <Tabs
            value={tabs}
            onChange={(event, newValue) => {
              setTabs(newValue);
            }}
            sx={{
              mt: '24px',
              '& .MuiTabs-indicator': {
                backgroundColor:
                  authUser?.theme === 'dark' ? '#D0D5DD' : 'black',
              },
              '& .MuiTab-root': {
                color: authUser?.theme === 'dark' ? '#D0D5DD' : 'black',
              },
              '& .Mui-selected': {
                color: 'primary.main',
              },
            }}
            aria-label="basic tabs example"
          >
            <Tab
              sx={{
                textTransform: 'none',
              }}
              label="Upload from computer"
              {...a11yProps(0)}
            />
            <Tab
              sx={{
                textTransform: 'none',
              }}
              label="FluxPrompt Uploads"
              {...a11yProps(1)}
            />
            <Tab
              sx={{
                textTransform: 'none',
              }}
              label="FluxPrompt Objects"
              {...a11yProps(2)}
            />
          </Tabs>

          {tabs === 0 && (
            <Box display="flex" flexDirection="column" gap="8px" mt="16px">
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap="8px"
              >
                <TextField
                  fullWidth
                  type="file"
                  inputProps={{ multiple: true }}
                  id={Math.random().toString()}
                  onChange={onChangeUploadFiles}
                  sx={{
                    '& .MuiInputLabel-root': {
                      color: '#9e9e9e',
                    },
                    '& label': {
                      backgroundColor: '#3f51b5',
                      color: '#fff',
                      padding: '10px 15px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    },
                    '& label:hover': {
                      backgroundColor: '#303f9f',
                    },
                  }}
                  variant="outlined"
                />

                {isLoading && <CircularProgress size="16px" />}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                {uploadedFiles && uploadedFiles?.length > 0 && (
                  <Typography variant="body2" mt={1}>
                    {uploadedFiles.length} file(s) selected
                  </Typography>
                )}

                {uploadedFiles && uploadedFiles?.length > 1 && (
                  <Button
                    onClick={() => {
                      setUploadedFiles([]);
                      setTotalFiles(0);
                    }}
                  >
                    Clear Files
                  </Button>
                )}
              </Box>

              <Box
                display="flex"
                flexDirection="column"
                alignItems="start"
                gap="8px"
              >
                {uploadedFiles &&
                  uploadedFiles?.length > 0 &&
                  uploadedFiles.map((file, index) => (
                    <Chip
                      sx={{
                        borderRadius: '4px',
                        color: '#3f51b5',
                        borderColor: '#3f51b5',
                        mt: '4px',
                      }}
                      deleteIcon={
                        <Trash color="#d32f2f" weight="duotone" size={14} />
                      }
                      onDelete={() => {
                        setUploadedFiles(
                          uploadedFiles?.filter(
                            (_uploadedFile, idx) => idx !== index,
                          ),
                        );
                        setTotalFiles(totalFiles - 1);
                      }}
                      key={index}
                      label={file?.name}
                      variant="outlined"
                    />
                  ))}
              </Box>
            </Box>
          )}

          {tabs === 1 && (
            <Box>
              <FormControl fullWidth>
                <InputLabel id="demo-simple-select-label">
                  Select Document
                </InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={formData.selectedDocument}
                  label="Select Document"
                  size="medium"
                  onChange={(e: any) => {
                    setFormData({
                      ...formData,
                      selectedDocument: e.target.value,
                    });
                  }}
                >
                  {documents.map((document: any, i: any) => (
                    <MenuItem value={document?.src} key={i}>
                      {document.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {tabs === 2 && (
            <Box>
              <FormControl
                sx={{
                  mt: '24px',
                }}
                fullWidth
              >
                <InputLabel id="demo-simple-select-label">
                  Flux Prompt Objects
                </InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  size="medium"
                  value={formData.selectedFluxPromptObject}
                  label="Flux Prompt Objects"
                  onChange={(e: any) => {
                    setFormData({
                      ...formData,
                      selectedFluxPromptObject: e.target.value,
                    });
                  }}
                >
                  {authUser?.objects.map((document: any, i: any) => (
                    <MenuItem value={document?.id} key={i}>
                      {document.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {formData.selectedFluxPromptObject && (
                <FormControl
                  sx={{
                    mt: '24px',
                  }}
                  fullWidth
                >
                  <InputLabel id="demo-simple-select-label">
                    Select Object from Selected Flux Prompt Object
                  </InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    size="medium"
                    id="demo-simple-select"
                    label=" Select Object from Selected Flux Prompt Object"
                    value={formData.fluxPromptObjectText}
                    onChange={(e: any) => {
                      setFormData({
                        ...formData,
                        fluxPromptObjectText: e.target.value,
                      });
                    }}
                  >
                    {authUser?.objects
                      ?.find(
                        item => item.id === formData.selectedFluxPromptObject,
                      )
                      ?.text?.map((document: any, i: any) => (
                        <MenuItem
                          value={document?.text}
                          title={document.text}
                          key={i}
                        >
                          Object {i + 1}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          )}
          {isLoading && (
            <Box marginTop={'8px'}>
              <Typography>{messageWait}</Typography>
            </Box>
          )}
          <Box
            display={'flex'}
            alignItems={'center'}
            gap={'10px'}
            justifyContent={isEditing ? 'space-between' : 'end'}
            mt={'60px'}
          >
            {selectVector?.name && isEditing && (
              <Button
                color="error"
                onClick={() => onDeleteNameSpace(selectVector?.name)}
                variant="outlined"
              >
                Delete Memory Pod
              </Button>
            )}
            <Box display={'flex'} alignItems={'center'} gap={'10px'}>
              <Button onClick={onHideModal} variant="outlined">
                Close
              </Button>
              <Button
                sx={{
                  boxShadow: 'none',
                }}
                onClick={() => {
                  setFinish(true);
                  setNotifyModal(true);
                  handleUpload();
                }}
                variant="contained"
                disabled={cantCreateMemoryPod}
              >
                {isEditing ? 'Edit' : 'Create'} memory Pod
              </Button>
            </Box>
          </Box>
        </>
      </KitModal>
    </>
  );
};

export default VectorMemoryUploadModal;
